/**
 * IconSelector Component
 * Allows users to customize icons for products with SVG upload or text abbreviation
 * Modern, minimalist design with background detection and removal
 */

import React, { useState, useCallback } from 'react';
import {
  Pencil,
  Upload,
  Type,
  X,
  Square,
  Circle,
  Palette,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  CategoryIcon,
  validateSVG,
  type CategoryIconConfig,
} from '@/features/product-library/components/CategoryIcon';
import {
  normalizeSvgForDynamicColor,
  detectSvgBackground,
  removeSvgBackground,
} from '@/lib/svgNormalizer';
import { sanitizeSVG } from '@/lib/sanitize';

// Color presets
const COLOR_PRESETS = [
  { name: 'Gray', fill: '#6B7280', border: '#374151' },
  { name: 'Blue', fill: '#3B82F6', border: '#1D4ED8' },
  { name: 'Green', fill: '#22C55E', border: '#15803D' },
  { name: 'Yellow', fill: '#EAB308', border: '#A16207' },
  { name: 'Orange', fill: '#F97316', border: '#C2410C' },
  { name: 'Red', fill: '#EF4444', border: '#B91C1C' },
  { name: 'Purple', fill: '#A855F7', border: '#7C3AED' },
  { name: 'Pink', fill: '#EC4899', border: '#BE185D' },
  { name: 'Teal', fill: '#14B8A6', border: '#0D9488' },
];

export interface IconData {
  iconType: 'svg' | 'abbreviation' | 'none';
  iconSvg?: string | null;
  abbreviation?: string | null;
  fillColor?: string;
  borderColor?: string;
  textColor?: string;
  iconShape?: 'square' | 'circle';
}

interface IconSelectorProps {
  currentIcon?: IconData;
  categoryIcon?: CategoryIconConfig;
  productName: string;
  onSave: (iconData: IconData) => Promise<void>;
  isSaving?: boolean;
  previewSize?: number;
  className?: string;
  /**
   * Whether to show the "Reset to default" button.
   * - true: Always show reset button when there's a custom icon
   * - false: Never show reset button (useful for custom categories with no default)
   * - undefined: Default behavior (show when hasCustomIcon is true)
   */
  showResetButton?: boolean;
}

export function IconSelector({
  currentIcon,
  categoryIcon,
  productName,
  onSave,
  isSaving = false,
  previewSize = 32,
  className,
  showResetButton,
}: IconSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [iconType, setIconType] = useState<'svg' | 'abbreviation' | 'none'>(
    currentIcon?.iconType || 'none',
  );
  const [iconSvg, setIconSvg] = useState<string | null>(currentIcon?.iconSvg || null);
  const [abbreviation, setAbbreviation] = useState(currentIcon?.abbreviation || '');
  const [fillColor, setFillColor] = useState(currentIcon?.fillColor || '#6B7280');
  const [borderColor, setBorderColor] = useState(currentIcon?.borderColor || '#374151');
  const [textColor, setTextColor] = useState(currentIcon?.textColor || '#FFFFFF');
  const [iconShape, setIconShape] = useState<'square' | 'circle'>(
    currentIcon?.iconShape || 'square',
  );
  const [svgWarnings, setSvgWarnings] = useState<string[]>([]);
  const [showCustomColors, setShowCustomColors] = useState(false);

  // Background detection state
  const [originalSvg, setOriginalSvg] = useState<string | null>(null);
  const [backgroundRemovedSvg, setBackgroundRemovedSvg] = useState<string | null>(null);
  const [hasDetectedBackground, setHasDetectedBackground] = useState(false);
  const [useBackgroundRemoved, setUseBackgroundRemoved] = useState(true);
  const [detectionReason, setDetectionReason] = useState<string>('');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setIconType(currentIcon?.iconType || 'none');
        setIconSvg(currentIcon?.iconSvg || null);
        setAbbreviation(currentIcon?.abbreviation || '');
        setFillColor(currentIcon?.fillColor || '#6B7280');
        setBorderColor(currentIcon?.borderColor || '#374151');
        setTextColor(currentIcon?.textColor || '#FFFFFF');
        setIconShape(currentIcon?.iconShape || 'square');
        setSvgWarnings([]);
        setShowCustomColors(false);
        // Reset background detection state
        setOriginalSvg(null);
        setBackgroundRemovedSvg(null);
        setHasDetectedBackground(false);
        setUseBackgroundRemoved(true);
        setDetectionReason('');
      }
      setIsOpen(open);
    },
    [currentIcon],
  );

  const handleSVGUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const svgContent = await file.text();
      const validation = validateSVG(svgContent);

      if (!validation.valid) {
        toast.error(validation.error || 'Invalid SVG file');
        e.target.value = '';
        return;
      }

      // Normalize the SVG for dynamic color support
      const svgToNormalize = validation.resizedSvg || svgContent;
      const normalizeResult = normalizeSvgForDynamicColor(svgToNormalize);
      const normalized = normalizeResult.svg;

      // Check for background in the normalized SVG
      const detection = detectSvgBackground(normalized);

      if (detection.hasBackground && detection.confidence !== 'low') {
        // Background detected - show comparison UI
        const removed = removeSvgBackground(normalized);
        const removedNormalized = normalizeSvgForDynamicColor(removed).svg;

        setOriginalSvg(normalized);
        setBackgroundRemovedSvg(removedNormalized);
        setHasDetectedBackground(true);
        setUseBackgroundRemoved(true);
        setDetectionReason(detection.reason);
        setIconSvg(removedNormalized);

        toast.info('Background detected in icon. Compare both versions below.', { duration: 4000 });
      } else {
        // No background - normal flow
        setOriginalSvg(null);
        setBackgroundRemovedSvg(null);
        setHasDetectedBackground(false);
        setDetectionReason('');
        setIconSvg(normalized);
      }

      setSvgWarnings(validation.warnings);
      setIconType('svg');
    } catch (error) {
      toast.error('Failed to read SVG file');
      e.target.value = '';
    }
  };

  // Handle background version selection
  const handleBackgroundChoice = (useRemoved: boolean) => {
    setUseBackgroundRemoved(useRemoved);
    const chosenSvg = useRemoved ? backgroundRemovedSvg : originalSvg;
    setIconSvg(chosenSvg);
  };

  const handleSave = async () => {
    const iconData: IconData = {
      iconType,
      iconSvg: iconType === 'svg' ? iconSvg : null,
      abbreviation: iconType === 'abbreviation' ? abbreviation : null,
      fillColor: iconType === 'abbreviation' ? fillColor : undefined,
      borderColor: iconType === 'abbreviation' ? borderColor : undefined,
      textColor: iconType === 'abbreviation' ? textColor : undefined,
      iconShape: iconType === 'abbreviation' ? iconShape : undefined,
    };

    try {
      await onSave(iconData);
      setIsOpen(false);
      toast.success('Icon updated');
    } catch (error) {
      toast.error('Failed to update icon');
    }
  };

  const handleRemove = async () => {
    try {
      await onSave({ iconType: 'none' });
      setIsOpen(false);
      toast.success('Custom icon removed');
    } catch (error) {
      toast.error('Failed to remove icon');
    }
  };

  const getDisplayIcon = (): CategoryIconConfig => {
    if (currentIcon?.iconType === 'svg' && currentIcon.iconSvg) {
      return {
        icon_type: 'svg',
        icon_svg: currentIcon.iconSvg,
        fill_color: '#6B7280',
      };
    }
    if (currentIcon?.iconType === 'abbreviation' && currentIcon.abbreviation) {
      return {
        icon_type: 'abbreviation',
        abbreviation: currentIcon.abbreviation,
        fill_color: currentIcon.fillColor || '#6B7280',
        border_color: currentIcon.borderColor || '#374151',
        text_color: currentIcon.textColor || '#FFFFFF',
        icon_shape: currentIcon.iconShape || 'square',
      };
    }
    return (
      categoryIcon || {
        icon_type: 'abbreviation',
        abbreviation: productName.slice(0, 3).toUpperCase(),
        fill_color: '#6B7280',
        border_color: '#374151',
        text_color: '#FFFFFF',
      }
    );
  };

  const getPreviewConfig = (): CategoryIconConfig => {
    if (iconType === 'svg' && iconSvg) {
      return {
        icon_type: 'svg',
        icon_svg: iconSvg,
        fill_color: '#6B7280',
      };
    }
    if (iconType === 'abbreviation') {
      return {
        icon_type: 'abbreviation',
        abbreviation: abbreviation || productName.slice(0, 3).toUpperCase(),
        fill_color: fillColor,
        border_color: borderColor,
        text_color: textColor,
        icon_shape: iconShape,
      };
    }
    return (
      categoryIcon || {
        icon_type: 'abbreviation',
        abbreviation: productName.slice(0, 3).toUpperCase(),
        fill_color: '#6B7280',
      }
    );
  };

  const hasCustomIcon = currentIcon?.iconType && currentIcon.iconType !== 'none';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex items-center gap-2">
        <CategoryIcon category={getDisplayIcon()} size={previewSize} />
        {hasCustomIcon && <span className="text-xs text-muted-foreground">(custom)</span>}
      </div>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setIsOpen(true)}>
          <Pencil className="h-3.5 w-3.5 mr-1" />
          Edit Icon
        </Button>

        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Icon for {productName}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Icon Type Selector - Segmented Control Style */}
            <div className="flex rounded-lg border bg-muted/30 p-1 gap-1">
              <button
                type="button"
                onClick={() => setIconType('none')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all',
                  iconType === 'none'
                    ? 'bg-background shadow-sm font-medium'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Default
              </button>
              <button
                type="button"
                onClick={() => setIconType('abbreviation')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all',
                  iconType === 'abbreviation'
                    ? 'bg-background shadow-sm font-medium'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Type className="h-3.5 w-3.5" />
                Text
              </button>
              <button
                type="button"
                onClick={() => setIconType('svg')}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all',
                  iconType === 'svg'
                    ? 'bg-background shadow-sm font-medium'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Upload className="h-3.5 w-3.5" />
                SVG
              </button>
            </div>

            {/* Preview - Centered and prominent */}
            <div className="flex justify-center py-4">
              <div className="relative">
                <div className="p-4 rounded-xl bg-muted/40 border border-border/50">
                  <CategoryIcon category={getPreviewConfig()} size={56} />
                </div>
                {iconType === 'none' && (
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs text-muted-foreground whitespace-nowrap">
                    Using category default
                  </span>
                )}
              </div>
            </div>

            {/* SVG Upload */}
            {iconType === 'svg' && (
              <div className="space-y-3 pt-2">
                {iconSvg ? (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border/50">
                    <span className="text-sm text-muted-foreground">SVG uploaded</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      onClick={() => {
                        setIconSvg(null);
                        setSvgWarnings([]);
                        // Clear background detection state
                        setOriginalSvg(null);
                        setBackgroundRemovedSvg(null);
                        setHasDetectedBackground(false);
                        setDetectionReason('');
                      }}
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed border-border/50 hover:border-primary/50 cursor-pointer transition-colors">
                    <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                    <span className="text-sm font-medium">Upload SVG</span>
                    <span className="text-xs text-muted-foreground mt-1">Click or drag file</span>
                    <input
                      type="file"
                      accept=".svg"
                      onChange={handleSVGUpload}
                      className="hidden"
                    />
                  </label>
                )}

                {/* Background comparison UI */}
                {hasDetectedBackground && originalSvg && backgroundRemovedSvg && (
                  <div className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center gap-2 mb-3 text-sm text-amber-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-xs">{detectionReason}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Original version */}
                      <label className="cursor-pointer">
                        <div
                          className={cn(
                            'border-2 rounded-lg p-2 transition-all',
                            !useBackgroundRemoved ? 'border-primary bg-primary/5' : 'border-muted',
                          )}
                        >
                          <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center overflow-hidden">
                            <div
                              className="w-full h-full flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:h-auto"
                              style={{ color: '#6B7280' }}
                              dangerouslySetInnerHTML={{ __html: sanitizeSVG(originalSvg) }}
                            />
                          </div>
                          <div className="flex items-center justify-center gap-1.5">
                            <input
                              type="radio"
                              name="bgChoice"
                              checked={!useBackgroundRemoved}
                              onChange={() => handleBackgroundChoice(false)}
                              className="accent-primary w-3 h-3"
                            />
                            <span className="text-[10px] font-medium">Original</span>
                          </div>
                        </div>
                      </label>

                      {/* Background removed version */}
                      <label className="cursor-pointer">
                        <div
                          className={cn(
                            'border-2 rounded-lg p-2 transition-all',
                            useBackgroundRemoved ? 'border-primary bg-primary/5' : 'border-muted',
                          )}
                        >
                          <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center overflow-hidden">
                            <div
                              className="w-full h-full flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:h-auto"
                              style={{ color: '#6B7280' }}
                              dangerouslySetInnerHTML={{
                                __html: sanitizeSVG(backgroundRemovedSvg),
                              }}
                            />
                          </div>
                          <div className="flex items-center justify-center gap-1.5">
                            <input
                              type="radio"
                              name="bgChoice"
                              checked={useBackgroundRemoved}
                              onChange={() => handleBackgroundChoice(true)}
                              className="accent-primary w-3 h-3"
                            />
                            <span className="text-[10px] font-medium">No Background</span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {svgWarnings.length > 0 && (
                  <div className="text-amber-600 text-xs p-2 bg-amber-50 rounded-md">
                    {svgWarnings.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Text Icon Options */}
            {iconType === 'abbreviation' && (
              <div className="space-y-4 pt-2">
                {/* Abbreviation Input */}
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Abbreviation</Label>
                  <Input
                    value={abbreviation}
                    onChange={e => setAbbreviation(e.target.value.toUpperCase().slice(0, 4))}
                    placeholder={productName.slice(0, 3).toUpperCase()}
                    maxLength={4}
                    className="h-9 text-center font-mono tracking-wider"
                  />
                </div>

                {/* Shape Toggle */}
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground min-w-[50px]">Shape</Label>
                  <div className="flex rounded-md border bg-muted/30 p-0.5">
                    <button
                      type="button"
                      onClick={() => setIconShape('square')}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-all',
                        iconShape === 'square'
                          ? 'bg-background shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Square className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIconShape('circle')}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1 rounded text-sm transition-all',
                        iconShape === 'circle'
                          ? 'bg-background shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <Circle className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Color Presets */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Color</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {COLOR_PRESETS.map(preset => (
                      <button
                        key={preset.name}
                        type="button"
                        className={cn(
                          'w-7 h-7 rounded-md border transition-all',
                          fillColor === preset.fill
                            ? 'ring-2 ring-primary ring-offset-1'
                            : 'hover:scale-105',
                        )}
                        style={{ backgroundColor: preset.fill, borderColor: preset.border }}
                        onClick={() => {
                          setFillColor(preset.fill);
                          setBorderColor(preset.border);
                        }}
                        title={preset.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Custom Colors - Collapsible */}
                <Collapsible open={showCustomColors} onOpenChange={setShowCustomColors}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Palette className="h-3.5 w-3.5" />
                      Custom colors
                      <ChevronDown
                        className={cn(
                          'h-3 w-3 transition-transform',
                          showCustomColors && 'rotate-180',
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3">
                    <div className="flex gap-3">
                      <div className="flex-1 space-y-1">
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Fill
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            type="color"
                            value={fillColor}
                            onChange={e => setFillColor(e.target.value)}
                            className="w-8 h-8 p-0.5 cursor-pointer"
                          />
                          <Input
                            value={fillColor}
                            onChange={e => setFillColor(e.target.value)}
                            className="flex-1 h-8 text-xs font-mono"
                          />
                        </div>
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Border
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            type="color"
                            value={borderColor}
                            onChange={e => setBorderColor(e.target.value)}
                            className="w-8 h-8 p-0.5 cursor-pointer"
                          />
                          <Input
                            value={borderColor}
                            onChange={e => setBorderColor(e.target.value)}
                            className="flex-1 h-8 text-xs font-mono"
                          />
                        </div>
                      </div>
                      <div className="flex-1 space-y-1">
                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Text
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            type="color"
                            value={textColor}
                            onChange={e => setTextColor(e.target.value)}
                            className="w-8 h-8 p-0.5 cursor-pointer"
                          />
                          <Input
                            value={textColor}
                            onChange={e => setTextColor(e.target.value)}
                            className="flex-1 h-8 text-xs font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}
          </div>

          <DialogFooter className="pt-4 gap-2 sm:gap-2">
            {/* Only show reset button if:
                - showResetButton is not explicitly false
                - AND there's a custom icon to reset */}
            {showResetButton !== false && hasCustomIcon && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                disabled={isSaving}
                className="text-destructive hover:text-destructive mr-auto"
              >
                Reset to default
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default IconSelector;
